import { ofType } from 'redux-observable';
import { BehaviorSubject, EMPTY, from, merge, of, Subject } from 'rxjs';
import { catchError, mergeMap, switchMap, take, takeUntil, takeWhile } from 'rxjs/operators';

import {
  bulkCostReviewApplyRecommendationsError,
  bulkCostReviewApplyRecommendationsSuccess,
  bulkCostReviewJobError,
  bulkCostReviewJobStarted,
  bulkCostReviewJobSuccess,
  BULK_COST_REVIEW_APPLY_RECOMMENDATIONS,
  COST_REVIEW_MODAL_CLOSE,
  COST_REVIEW_MODAL_OPEN,
  RELOAD_COST_REVIEW,
} from './bulk.cost-review.actions';
import { bulkApplyRecommendations, reloadUpdatedFulfillmentPlans } from '../../recommendations/recommendations.observables';
import * as BulkApi from '../../bulk/bulk.api';
import { FeatureFlags } from '../../../feature-flags/feature-flags';
import * as Actions from '../../bulk/purchase/bulk.purchase.actions';
import { getSummary } from '../../../fulfillment-planner/rate-recommendation/rate-recommendations.api';
import { LogService } from '../../../services/log-service';
import { startCostReviewGetMessages } from '../../cost-review';
import Global from '../../global-selectors';
import { HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING } from '../../../wallet/utilities/constants';
import { getIsBulkRateShopperRecommendationsEnabled } from '../../../hooks/use-is-bulk-rate-shopper-recommendations-enabled';

const PUSHER_TIMEOUT_MS = 10000;

const waitForReloadCostReview = (action$, reloadCostReview, costReviewClosed$) => {
  return action$.pipe(
    ofType(RELOAD_COST_REVIEW),
    take(1),
    takeUntil(costReviewClosed$),
    switchMap(() => {
      reloadCostReview();
      return EMPTY;
    }),
  );
};

export const waitForApplyRecommendations = (action$, getState, reloadCostReview, costReviewClosed$) => {
  return action$.pipe(
    ofType(BULK_COST_REVIEW_APPLY_RECOMMENDATIONS),
    takeUntil(costReviewClosed$),
    mergeMap(
      ({ jobGuid, bulkUpdateJobGuid, serviceFilters, insuranceFilters, rateShopperFilters, isRateShopperRecommendationsEnabled }) => {
        const request = {
          bulkUpdateJobGuid,
          serviceFilters,
          insuranceFilters,
          rateShopperFilters,
          isRateShopperRecommendationsEnabled,
        };

        const handleSuccess = ({ amountSaved, updatedFulfillmentPlanIds }) => {
          return merge(
            reloadUpdatedFulfillmentPlans(updatedFulfillmentPlanIds),
            of(bulkCostReviewApplyRecommendationsSuccess(jobGuid, amountSaved)),
            waitForReloadCostReview(action$, reloadCostReview, costReviewClosed$),
          );
        };

        const handleError = () => of(bulkCostReviewApplyRecommendationsError(jobGuid));

        return bulkApplyRecommendations(getState, request, handleSuccess, handleError);
      },
    ),
  );
};

const setCostReviewData = ({ jobGuid, fulfillmentPlanIds, costReviewData, shouldGetRecommendations, getState }) => {
  const setCostReviewDataWithRecommendations = (recommendationSummary) =>
    Actions.openCostReview(jobGuid, fulfillmentPlanIds, [], [], costReviewData, false, false, recommendationSummary);

  const state = getState();
  const isSwitchAndSaveRecommendationsEnabled = new FeatureFlags({ state }).enableSwitchAndSaveRateRecommendations;

  if (!isSwitchAndSaveRecommendationsEnabled || !shouldGetRecommendations) {
    return of(setCostReviewDataWithRecommendations());
  }

  const isBulkRateShopperRecommendationsEnabled = getIsBulkRateShopperRecommendationsEnabled(state);

  return from(getSummary({ fulfillmentPlanIds, isRateShopperRecommendationsEnabled: isBulkRateShopperRecommendationsEnabled })).pipe(
    mergeMap((response) => of(setCostReviewDataWithRecommendations(response.data))),
    catchError(() => of(setCostReviewDataWithRecommendations())),
  );
};

const processCostReviewMessage = ({
  costReviewData,
  jobGuid,
  fulfillmentPlanIds,
  isReload,
  action$,
  getState,
  reloadCostReview,
  costReviewClosed$,
}) => {
  return merge(
    setCostReviewData({
      jobGuid,
      fulfillmentPlanIds,
      costReviewData,
      // Product only wants to show recommendations on the first pass. If the user applies any recommendations, we don't want to refetch and show them again.
      shouldGetRecommendations: !isReload,
      getState,
    }),
    of(bulkCostReviewJobSuccess()),
    waitForApplyRecommendations(action$, getState, reloadCostReview, costReviewClosed$),
  );
};

export const costReviewModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(COST_REVIEW_MODAL_OPEN),
    mergeMap(({ fulfillmentPlanIds }) => {
      const { enableCostReviewModalRefactor } = new FeatureFlags({
        state: getState(),
      });
      if (!enableCostReviewModalRefactor) {
        return EMPTY;
      }

      const costReviewTimeout$ = new Subject();
      const startCostReviewSubject = new BehaviorSubject({
        isRetry: false,
        isReload: false,
      });
      const onRetryFailed = () => costReviewTimeout$.next(bulkCostReviewJobError());
      const reloadCostReview = () => startCostReviewSubject.next({ isRetry: false, isReload: true });
      const costReviewClosed$ = action$.pipe(ofType(COST_REVIEW_MODAL_CLOSE));

      return startCostReviewSubject.pipe(
        takeUntil(costReviewClosed$),
        mergeMap(({ isRetry, isReload }) => {
          const logContext = {};

          const retryOrConcludeFailure = ({ jobGuids, errorOnMessage }) => {
            if (!errorOnMessage) {
              LogService.logInfo(
                !isRetry
                  ? 'Timeout waiting for Cost Review pusher message. Automatically retrying...'
                  : 'Timeout waiting for Cost Review pusher message (retry). Showing error to user.',
                {
                  ...logContext,
                  jobGuids,
                },
              );
            }
            if (!isRetry) {
              startCostReviewSubject.next({ isRetry: true, isReload });
            } else {
              onRetryFailed();
            }
          };

          const hasAcceptedUpsLabelTerms =
            Global.selectSellerSellerSetting(getState(), { settingName: HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING }) === 'true';

          return startCostReviewGetMessages({
            apiCallback: () => BulkApi.costReview(fulfillmentPlanIds, hasAcceptedUpsLabelTerms),
            pusherTimeoutMs: PUSHER_TIMEOUT_MS,
            onError: retryOrConcludeFailure,
            logContext,
          }).pipe(
            mergeMap(({ jobGuids, findMessage$ }) => {
              const [jobGuid] = jobGuids || [];

              const next$ = of(bulkCostReviewJobStarted(jobGuid));
              const retry$ = isRetry ? costReviewTimeout$ : EMPTY;
              const stopListeningForCostReviewMessages$ = merge(costReviewTimeout$, costReviewClosed$);

              return merge(
                retry$,
                next$,
                findMessage$.pipe(
                  takeUntil(stopListeningForCostReviewMessages$),
                  take(1),
                  mergeMap(({ pusherData }) => {
                    return of({
                      pusherData,
                      jobGuid,
                      isReload,
                    });
                  }),
                ),
              );
            }),
          );
        }),
        takeWhile((next) => {
          // Currently we can only reload once, so we can stop listening for messages after the first reload.
          const isSuccessfulReload = next?.isReload && next?.pusherData;
          return !isSuccessfulReload;
        }, true),
        switchMap((next) => {
          // if no pusher data, just pass through
          if (!next.pusherData) {
            return of(next);
          }
          const { jobGuid, pusherData, isReload } = next;

          return processCostReviewMessage({
            costReviewData: pusherData,
            jobGuid,
            fulfillmentPlanIds,
            isReload,
            action$,
            getState,
            reloadCostReview,
            costReviewClosed$,
          });
        }),
        catchError(() => of(bulkCostReviewJobError())),
      );
    }),
  );
};
